import React, { useEffect, useState, ReactNode, useMemo } from 'react';
import { ThemeProvider as StyledThemeProvider } from 'styled-components';
import { ThemeProvider as MuiThemeProvider, createTheme } from '@mui/material';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import GlobalStyles from '@/styles/theme';
import { ThemeContext, type Theme, type ThemeContextType } from './ThemeContext';

const THEME_STORAGE_KEY = 'pclash-theme';
const DEFAULT_THEME: Theme = 'light';

// Emotion cache for MUI
const emotionCache = createCache({ key: 'mui' });

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [theme, setThemeState] = useState<Theme>(DEFAULT_THEME);
  const page = document.documentElement;

  // Temporarily disable transitions when changing theme to prevent flickering
  const stopTransition = () => {
    page.classList.add('no-transition');
    setTimeout(() => page.classList.remove('no-transition'), 100);
  };

  // Initialize theme from localStorage or use default
  useEffect(() => {
    const savedTheme = localStorage.getItem(THEME_STORAGE_KEY) as Theme;
    if (savedTheme && (savedTheme === 'light' || savedTheme === 'dark')) {
      setThemeState(savedTheme);
    } else {
      setThemeState(DEFAULT_THEME);
    }
  }, []);

  // Apply theme to document when theme changes
  useEffect(() => {
    page.setAttribute('data-theme', theme);

    // Also set a class for backward compatibility if needed
    page.className = page.className
      .replace(/theme-\w+/g, '') + ` theme-${theme}`;
  }, [theme, page]);

  const setTheme = (newTheme: Theme) => {
    stopTransition();
    setThemeState(newTheme);
    localStorage.setItem(THEME_STORAGE_KEY, newTheme);
  };

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
  };

  // MUI theme configuration
  const muiTheme = useMemo(() => createTheme({
    palette: {
      mode: theme,
      primary: {
        main: '#FDCA40', // --accent / --primary-yellow
      },
      secondary: {
        main: '#66bb6a', // --secondary-green
      },
      background: {
        default: theme === 'light' ? '#FAFAF8' : '#111312',
        paper: theme === 'light' ? '#FFFFFF' : '#1a1c1b',
      },
    },
    typography: {
      fontFamily: "'Open Sans', sans-serif",
    },
  }), [theme]);

  // Styled-components theme object
  const styledTheme = useMemo(() => ({ theme }), [theme]);

  const value: ThemeContextType = {
    theme,
    setTheme,
    toggleTheme,
  };

  return (
    <ThemeContext.Provider value={value}>
      <CacheProvider value={emotionCache}>
        <StyledThemeProvider theme={styledTheme}>
          <MuiThemeProvider theme={muiTheme}>
            <GlobalStyles />
            {children}
          </MuiThemeProvider>
        </StyledThemeProvider>
      </CacheProvider>
    </ThemeContext.Provider>
  );
};
